// Web landings the app and Stripe point at: payment returns, deep links (/pulse/:id, /live/:id, /p/:code),
// shared trip plans and the global creators leaderboard. Every Stripe success/cancel URL has a page here.
import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, MapPin, Plane, Hotel as HotelIcon, Radio, Ticket, Trophy, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CreatorAvatar } from "@/components/stage/PostCard";

const cx = (...a) => a.filter(Boolean).join(" ");

export function ReturnCard({ state = "ok", title, body, children }) {
  const Icon = state === "ok" ? CheckCircle2 : state === "wait" ? Loader2 : state === "off" ? XCircle : Clock;
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 py-16 text-center" data-testid={"return-" + state}>
      <span className={cx("grid h-16 w-16 place-items-center rounded-full", state === "ok" ? "bg-emerald-500/10 text-emerald-600" : state === "off" ? "bg-muted text-muted-foreground" : "bg-[#E0007A]/10 text-[#E0007A]")}>
        <Icon size={30} className={state === "wait" ? "animate-spin" : ""} />
      </span>
      <h1 className="mt-5 font-serif-display text-2xl font-bold">{title}</h1>
      {body && <p className="mt-2 text-muted-foreground">{body}</p>}
      {children && <div className="mt-6 flex w-full flex-col gap-2">{children}</div>}
    </div>
  );
}

/** Polls fn every 2 s until isDone(result) or 15 tries. */

// Ask the backend to confirm a Stripe Checkout session straight away (the webhook may lag). Every status endpoint is
// idempotent, so trying the likely ones is safe; errors are ignored.
function confirmReturn(paths) { paths.forEach((path) => api(path).catch(() => {})); }

export function usePoll(fn, isDone, enabled) {
  const [res, setRes] = useState(null);
  const [state, setState] = useState(enabled ? "wait" : "idle");
  const ref = useRef({ fn, isDone });
  ref.current = { fn, isDone };
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true; let n = 0;
    const tick = async () => {
      try { const r = await ref.current.fn(); if (!alive) return; setRes(r); if (ref.current.isDone(r)) { setState("ok"); return; } } catch { /* retry */ }
      if (++n < 15 && alive) setTimeout(tick, 2000); else if (alive) setState("slow");
    };
    tick();
    return () => { alive = false; };
  }, [enabled]);
  return [state, res];
}

export function WalletReturn() {
  usePageMeta("Payment", "Payment status on Fable +.");
  const [p] = useSearchParams();
  const navigate = useNavigate();
  const cancel = p.get("cancel");
  const studio = p.get("kind") === "studio";
  useEffect(() => { const sid = p.get("session_id"); const kind = p.get("kind"); if (!sid || cancel) return; const e = encodeURIComponent(sid); confirmReturn(kind === "gift" ? ["/gifts/status/" + e] : kind === "studio" ? ["/studio/checkout-status/" + e] : ["/wallet/checkout-status/" + e]); }, [p, cancel]);
  useEffect(() => { if (cancel) return undefined; const t = setTimeout(() => navigate(studio ? "/studio" : "/wallet", { replace: true }), 1800); return () => clearTimeout(t); }, [cancel, studio, navigate]);
  return cancel
    ? <ReturnCard state="off" title="Payment cancelled" body="No charge was made."><Link to={studio ? "/studio" : "/wallet"}><Button className="w-full rounded-full">Back</Button></Link></ReturnCard>
    : <ReturnCard title="Payment received" body={studio ? "Your Studio credits are on the way. Taking you back…" : "Your wallet is updating. Taking you back…"} />;
}

export function PaymentSuccess() {
  usePageMeta("Booking confirmed", "Your Fable + booking.");
  const [p] = useSearchParams();
  const sid = p.get("session_id");
  const [state, res] = usePoll(() => api("/payments/stripe/status/" + encodeURIComponent(sid)), (r) => r && r.payment_status === "paid", !!sid);
  const b = res && res.booking;
  if (!sid) return <ReturnCard state="off" title="Nothing to confirm" body="We couldn't find a payment to check."><Link to="/bookings"><Button className="w-full rounded-full">My bookings</Button></Link></ReturnCard>;
  if (state === "wait") return <ReturnCard state="wait" title="Confirming your payment…" body="This only takes a moment." />;
  if (state === "ok") return (
    <ReturnCard title="You're booked" body={b ? (b.listing_title || b.title || "Your booking") + " is confirmed. The provider has been told." : "Your booking is confirmed."}>
      <Link to="/bookings"><Button className="w-full rounded-full bg-[#E0007A] hover:bg-[#c4006a]">View my bookings</Button></Link>
      <Link to="/"><Button variant="outline" className="w-full rounded-full">Back to home</Button></Link>
    </ReturnCard>
  );
  return <ReturnCard state="slow" title="Still confirming" body="Your bank is taking a little longer. Your booking will appear in My bookings as soon as Stripe confirms it."><Link to="/bookings"><Button className="w-full rounded-full">Check my bookings</Button></Link></ReturnCard>;
}

export function PaymentCancel() {
  usePageMeta("Payment cancelled", "No charge was made.");
  const [p] = useSearchParams();
  const lid = p.get("listing_id");
  return (
    <ReturnCard state="off" title="Payment cancelled" body="No charge was made. You can try again whenever you're ready.">
      {lid && <Link to={"/listing/" + lid}><Button className="w-full rounded-full bg-[#E0007A] hover:bg-[#c4006a]">Back to the listing</Button></Link>}
      <Link to="/"><Button variant="outline" className="w-full rounded-full">Back to home</Button></Link>
    </ReturnCard>
  );
}

export function PromoteReturn() {
  usePageMeta("Promotion", "Promotion payment on Fable +.");
  const [p] = useSearchParams();
  const navigate = useNavigate();
  const cancel = p.get("cancel");
  const to = p.get("to") || "";
  const sid = p.get("session_id");
  const dest = to === "collab" && p.get("deal") ? "/collabs/deals/" + p.get("deal") + (sid ? "?session_id=" + encodeURIComponent(sid) : "") : to === "ads" ? "/partner/ads" : to.startsWith("/provider") || !to.startsWith("/") ? "/partner" : to;
  useEffect(() => { if (!sid || cancel || to === "collab") return; const e = encodeURIComponent(sid); confirmReturn(to === "ads" ? ["/provider/ads/status/" + e, "/provider/ads/topup/status/" + e] : ["/provider/promotions/status/" + e]); }, [sid, cancel, to]);
  useEffect(() => { const t = setTimeout(() => navigate(dest, { replace: true }), 1600); return () => clearTimeout(t); }, [dest, navigate]);
  return <ReturnCard state={cancel ? "off" : "ok"} title={cancel ? "Promotion not started" : "Promotion paid"} body={cancel ? "No charge was made." : "It goes live as soon as Stripe confirms. Taking you back…"} />;
}

export function MealPlansReturn() {
  usePageMeta("Meal plan", "Your Fable + meal plan.");
  const [p] = useSearchParams();
  const sid = p.get("session_id");
  const cancel = p.get("cancel") === "1";
  const [state, res] = usePoll(() => api("/meal-plans/checkout/" + encodeURIComponent(sid)), (r) => r && r.paid, !!sid && !cancel);
  if (cancel || !sid) return <ReturnCard state="off" title="Checkout cancelled" body="No charge was made."><Link to="/"><Button className="w-full rounded-full">Back to home</Button></Link></ReturnCard>;
  if (state === "wait") return <ReturnCard state="wait" title="Setting up your plan…" />;
  return (
    <ReturnCard state={state === "ok" ? "ok" : "slow"} title={state === "ok" ? "Your meal plan is on" : "Almost there"} body={state === "ok" ? ((res && res.subscription && res.subscription.title) || "Your plan") + " is active. Pick meals and delivery days in the Fable + app." : "Stripe is still confirming. It will show in the app shortly."}>
      <Link to="/get-the-app"><Button className="w-full rounded-full bg-[#E0007A] hover:bg-[#c4006a]">Open the app</Button></Link>
    </ReturnCard>
  );
}

export function DbsReturn() {
  usePageMeta("DBS check", "DBS check payment.");
  const [p] = useSearchParams();
  const cancel = p.get("cancel");
  return (
    <ReturnCard state={cancel ? "off" : "ok"} title={cancel ? "DBS check not paid" : "DBS check paid"} body={cancel ? "No charge was made. Start again from your partner dashboard." : "We'll email you the link to complete your check. The badge appears on your profile once it clears."}>
      <Link to="/partner"><Button className="w-full rounded-full">Partner dashboard</Button></Link>
    </ReturnCard>
  );
}

export function GroupReturn() {
  usePageMeta("Group payment", "Your share of a group plan.");
  const [p] = useSearchParams();
  if (p.get("split")) return <Navigate to={"/splits/" + p.get("split") + "?" + p.toString()} replace />;
  const paid = !!p.get("session_id");
  return (
    <ReturnCard state={paid ? "ok" : "off"} title={paid ? (p.get("cover") ? "You covered the group" : "Your share is paid") : "Payment not finished"} body="The group chat updates as soon as Stripe confirms.">
      <Link to="/messages"><Button className="w-full rounded-full">Open messages</Button></Link>
    </ReturnCard>
  );
}

export function CourseReturn() {
  const { id } = useParams();
  const [p] = useSearchParams();
  const cs = p.get("circle_session");
  return <Navigate to={"/circles/" + id + "?tab=classroom" + (cs ? "&circle_session=" + encodeURIComponent(cs) : "")} replace />;
}

export function PulseRedirect() {
  const { id } = useParams();
  return <Navigate to={"/stage/post/" + id} replace />;
}

const HLS_SRC = "https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.13/hls.min.js";
function loadHls() {
  if (window.Hls) return Promise.resolve(window.Hls);
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = HLS_SRC; s.async = true; s.crossOrigin = "anonymous";
    s.onload = () => resolve(window.Hls); s.onerror = reject;
    document.head.appendChild(s);
  });
}

function HlsVideo({ src }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !src) return undefined;
    let hls = null;
    if (el.canPlayType("application/vnd.apple.mpegurl")) { el.src = src; el.play().catch(() => null); return undefined; }
    loadHls().then((Hls) => {
      if (!Hls || !Hls.isSupported()) { el.src = src; return; }
      hls = new Hls({ lowLatencyMode: true });
      hls.loadSource(src); hls.attachMedia(el);
      el.play().catch(() => null);
    }).catch(() => { el.src = src; });
    return () => { if (hls) hls.destroy(); };
  }, [src]);
  return <video ref={ref} controls playsInline muted autoPlay className="aspect-video w-full rounded-2xl bg-black" data-testid="live-video" />;
}

export function LiveWatch() {
  const { id } = useParams();
  const [p] = useSearchParams();
  const [s, setS] = useState(undefined);
  usePageMeta((s && s.title) || "Live", "Watch live on Fable + Stage.");
  useEffect(() => {
    let alive = true;
    const load = () => api("/live/" + id).then((d) => alive && setS(d)).catch(() => alive && setS(null));
    load();
    if (p.get("session_id")) { toast.success("Ticket confirmed. Enjoy the show."); const t = setTimeout(load, 2500); return () => { alive = false; clearTimeout(t); }; }
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [id, p]);
  if (s === undefined) return <div className="mx-auto max-w-4xl px-4 py-8"><Skeleton className="aspect-video rounded-2xl" /></div>;
  if (s === null) return <ReturnCard state="off" title="This stream isn't available" body="It may have ended or been made private."><Link to="/stage/live"><Button className="w-full rounded-full">See who's live</Button></Link></ReturnCard>;
  const host = s.creator || s.host || s.author || s.owner || {};
  const live = s.status === "live";
  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6" data-testid={"live-" + id}>
      {live && s.hls_url ? <HlsVideo src={s.hls_url} /> : (
        <div className="grid aspect-video w-full place-items-center rounded-2xl bg-gradient-to-br from-[#E0007A]/25 to-[#8B3CF0]/30 text-center">
          <div className="px-6">
            {live ? <Ticket className="mx-auto" /> : <Radio className="mx-auto" />}
            <p className="mt-2 font-semibold">{live ? "This live needs a ticket or membership" : s.status === "scheduled" ? "Starts soon" : "This live has ended"}</p>
            <p className="text-sm text-muted-foreground">{live ? "Get access in the Fable + app." : "Highlights and replays live on the creator's profile."}</p>
          </div>
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <CreatorAvatar author={host} size={44} />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-bold">{s.title || "Live"}</h1>
          <p className="text-sm text-muted-foreground">{host.display_name || host.handle || "Creator"}{live ? " · live now" : ""}{s.viewers != null ? " · " + s.viewers + " watching" : ""}</p>
        </div>
        {host.handle && <Link to={"/creators/" + host.handle}><Button variant="outline" className="rounded-full">Profile</Button></Link>}
      </div>
      <p className="mt-4 rounded-xl bg-muted/60 px-3 py-2 text-xs text-muted-foreground">Chat, gifts and going live happen in the Fable + app.</p>
    </div>
  );
}

/** Poster / QR deep link: /p/CODE adds the provider to your Plus + favourites and opens their listing. */
export function PlusCode() {
  const { code } = useParams();
  const { user, bootstrapped } = useAuth();
  const navigate = useNavigate();
  const [err, setErr] = useState(null);
  const ran = useRef(false);
  useEffect(() => {
    if (bootstrapped === false || ran.current) return;
    if (!user) { navigate("/login", { replace: true, state: { from: "/p/" + code } }); return; }
    ran.current = true;
    api("/plus/redeem", { method: "POST", body: { code } }).then((r) => navigate("/listing/" + r.listing.id, { replace: true })).catch((x) => setErr(x.message || "That code didn't work."));
  }, [user, bootstrapped, code, navigate]);
  if (err) return <ReturnCard state="off" title="Code not recognised" body={err}><Link to="/marketplace"><Button className="w-full rounded-full">Browse the marketplace</Button></Link></ReturnCard>;
  return <ReturnCard state="wait" title="Opening…" />;
}

const KIND_ICON = { flights: Plane, hotel: HotelIcon, booking: CheckCircle2, events: Ticket };
function money(n, cur) { try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: cur || "GBP", maximumFractionDigits: 0 }).format(n); } catch { return "£" + Math.round(n); } }

/** A trip plan someone shared from Yasmin. Read-only; partner links open the partner. */
export function SharedPlan() {
  const { token } = useParams();
  const [plan, setPlan] = useState(undefined);
  usePageMeta((plan && plan.title) || "Shared plan", "A trip plan shared from Yasmin on Fable +.");
  useEffect(() => { api("/yasmin/shared/" + encodeURIComponent(token)).then(setPlan).catch(() => setPlan(null)); }, [token]);
  if (plan === undefined) return <div className="mx-auto max-w-3xl px-4 py-8"><Skeleton className="h-10 w-2/3" /><Skeleton className="mt-4 h-40 rounded-2xl" /></div>;
  if (plan === null) return <ReturnCard state="off" title="This plan isn't shared any more" body="Ask whoever sent it for a fresh link."><Link to="/travel"><Button className="w-full rounded-full">Plan your own trip</Button></Link></ReturnCard>;
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6" data-testid="shared-plan">
      <p className="text-sm font-semibold text-[#E0007A]">Shared by {plan.owner || "a friend"}</p>
      <h1 className="mt-1 font-serif-display text-3xl font-bold">{plan.title}</h1>
      <ol className="mt-6 space-y-4">
        {(plan.parts || []).map((part) => {
          const Icon = KIND_ICON[part.kind] || MapPin;
          const flight = (part.options || [])[0];
          return (
            <li key={part.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#E0007A]/10 text-[#E0007A]"><Icon size={18} /></span>
                <div className="min-w-0 flex-1"><p className="truncate font-semibold">{part.title || part.label || part.kind}</p><p className="text-xs text-muted-foreground">{[part.date, part.city, part.nights ? part.nights + " nights" : null].filter(Boolean).join(" · ")}</p></div>
              </div>
              {part.note && <p className="mt-2 text-sm text-muted-foreground">{part.note}</p>}
              {flight && <p className="mt-3 text-sm"><b>{money(flight.price, flight.currency)}</b> · {flight.airline} · {flight.stops ? flight.stops + " stop" + (flight.stops > 1 ? "s" : "") : "direct"}</p>}
              {(part.hotels || []).length > 0 && (
                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                  {part.hotels.slice(0, 4).map((h) => (
                    <li key={h.id}><a href={h.partner_url} target="_blank" rel="noopener sponsored" className="flex items-center gap-3 rounded-xl border border-border p-2 hover:bg-muted/50">
                      {h.image_url ? <img src={h.image_url} alt="" className="h-12 w-12 rounded-lg object-cover" loading="lazy" /> : <span className="h-12 w-12 rounded-lg bg-muted" />}
                      <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{h.name}</span><span className="text-xs text-muted-foreground">{h.price ? money(h.price, h.currency) + " · " : ""}{h.stars ? h.stars + "★" : h.kind}</span></span>
                    </a></li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
      <div className="mt-8 rounded-2xl bg-muted/60 p-5 text-center"><p className="font-semibold">Want Yasmin to plan yours?</p><Link to="/travel/plan"><Button className="mt-3 rounded-full bg-[#E0007A] hover:bg-[#c4006a]">Plan a trip</Button></Link></div>
    </div>
  );
}

/** Global top creators — ranked by coins received from gifts and tips. Coins only, never pounds. */
export function CreatorsLeaderboard() {
  usePageMeta("Top creators", "The creators fans support most on Fable + Stage.");
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(undefined);
  useEffect(() => { let alive = true; setData(undefined); api("/leaderboards/creators?period=" + period).then((d) => alive && setData(d)).catch(() => alive && setData(null)); return () => { alive = false; }; }, [period]);
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6" data-testid="creators-leaderboard">
      <h1 className="flex items-center gap-2 font-serif-display text-3xl font-bold"><Trophy className="text-amber-500" /> Top creators</h1>
      <p className="mt-1 text-muted-foreground">Ranked by the coins fans send. Support someone and watch them climb.</p>
      <div className="mt-5 flex gap-2" role="tablist">{[["week", "This week"], ["month", "This month"], ["all", "All time"]].map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={period === k} onClick={() => setPeriod(k)} className={cx("rounded-full border px-3.5 py-1.5 text-sm font-semibold", period === k ? "border-transparent bg-[#E0007A] text-white" : "border-border bg-card hover:bg-muted")}>{l}</button>)}</div>
      {data === undefined ? <Skeleton className="mt-5 h-80 rounded-2xl" /> : !data || !(data.items || []).length ? <p className="mt-8 text-center text-muted-foreground">No gifts in this period yet.</p> : (
        <ol className="mt-5 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
          {data.items.map((r) => (
            <li key={r.user_id}>
              <Link to={"/creators/" + r.handle} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50">
                <span className={cx("w-7 text-center text-lg font-extrabold", r.rank === 1 ? "text-amber-500" : r.rank <= 3 ? "text-[#E0007A]" : "text-muted-foreground")}>{r.rank}</span>
                <CreatorAvatar author={r} size={40} />
                <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{r.display_name}</span><span className="text-xs text-muted-foreground">@{r.handle}{r.category ? " · " + r.category : ""}</span></span>
                <span className="text-right"><span className="block font-bold">{(r.coins || 0).toLocaleString("en-GB")} coins</span><span className="text-xs text-muted-foreground">{r.supporters} supporters</span></span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
